import { FC, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { useMediaQuery } from 'react-responsive';
import { useQuery } from '@tanstack/react-query';
import { Icon, AudioPlayer } from 'src/components';
import { MyWordsCounter } from 'src/apps/MyWords/MyWordsCounter';
import { DropdownSectionProps } from 'src/components/DropdownMenu';
import { ExperienceType } from 'src/context/experience';
import theme from 'src/utils/theme';
import { MeApi } from 'src/api';
import { LearnableDetails } from 'src/api/learnableDetails/types';
import cleanLanguageName from 'src/utils/language/cleanLanguageName';
import { BuddyLockedModal } from 'src/apps/Buddies/BuddyLockedModal';
import { PronunciationModal } from 'src/apps/Buddy/PronunciationBuddy/PronunciationModal';
import { FeatureUpsellModal } from 'src/apps/Monetisation/FeatureUpsellModal';
import { romanizationTextAttrs } from 'src/utils/pinyinFont';
import VideoSection from './VideoSection';
import * as S from './styles';

const msgs = defineMessages({
  audio: { id: 'words_detail_audio' },
  known: { id: 'words_detail_marked_known' },
  difficult: { id: 'words_detail_marked_difficult' },
  pronunciationPractice: { id: 'words_detail_pronunciation_buddy' },
  aiBuddy: { id: 'words_detail_buddies' },
  romanisation: { id: 'words_detail_romanisation' },
  kana: { id: 'words_detail_kana' },
  literalTranslation: { id: 'words_detail_literal_translation' },
  markAsKnown: { id: 'scenarioDetails_markWordAsKnown' },
  unmarkAsKnown: { id: 'scenarioDetails_unmarkWordAsKnown' },
  markAsDifficult: { id: 'scenarioDetails_markWordAsDifficult' },
  unmarkAsDifficult: { id: 'scenarioDetails_removeWordAsDifficult' },
});

export interface WordDetailsProps {
  experienceType: ExperienceType;
  learnableDetails: LearnableDetails;
  targetLanguageName?: string;
  sourceLanguageName?: string;
  isKnown?: boolean;
  isDifficult?: boolean;
  hideDifficultToggle?: boolean;
  hideMarkAsKnownToggle?: boolean;
  canUsePronunciationBuddy?: boolean;
  isPronunciationBuddyLocked?: boolean;
  onMarkAsKnown?: (learnableId: number, isKnown: boolean) => void;
  onMarkAsDifficult?: (learnableId: number, isDifficult: boolean) => void;
  onActivityEngagement: () => void;
}

export const WordDetails: FC<WordDetailsProps> = ({
  experienceType,
  targetLanguageName,
  sourceLanguageName,
  learnableDetails,
  isKnown = false,
  isDifficult = false,
  hideDifficultToggle,
  hideMarkAsKnownToggle,
  canUsePronunciationBuddy,
  isPronunciationBuddyLocked,
  onMarkAsKnown,
  onMarkAsDifficult,
  onActivityEngagement,
}) => {
  const intl = useIntl();
  const [isMarkedAsKnown, setIsMarkedAsKnown] = useState(isKnown);
  const [isMarkedAsDifficult, setIsMarkedAsDifficult] = useState(isDifficult);
  const [isOpenLockedModal, setIsOpenLockedModal] = useState(false);
  const [isOpenPronunciationModal, setIsOpenPronunciationModal] = useState(false);
  const isMobile = useMediaQuery({ maxWidth: theme.sz.xl });
  const {
    learnableId,
    itemType,
    videoUrls,
    audioUrls,
    targetValue,
    sourceValue,
    romanization,
    kana,
    literalTranslation,
  } = learnableDetails;

  const { data, isLoading } = useQuery({
    queryKey: ['getLearnableWordCounters', learnableId],
    queryFn: () => MeApi.getLearnableWordCounters(learnableId),
    enabled: !!learnableId,
  });

  const { wordCounters } = data || {};

  const handleMarkAsKnown = () => {
    /* istanbul ignore else: empty else branch */
    if (learnableId && onMarkAsKnown) {
      onMarkAsKnown(learnableId, isKnown);
      setIsMarkedAsKnown(!isKnown);
    }
  };

  const handleMarkAsDifficult = () => {
    /* istanbul ignore else: empty else branch */
    if (learnableId && onMarkAsDifficult) {
      onMarkAsDifficult(learnableId, isMarkedAsDifficult);
      setIsMarkedAsDifficult(!isMarkedAsDifficult);
    }
  };

  const dropdownSections = [
    ...(hideMarkAsKnownToggle
      ? []
      : [
          {
            dropdownItems: [
              {
                type: 'icon',
                iconCode: isMarkedAsKnown ? 'tickFilled' : 'tickOutline',
                labelText: intl.formatMessage(
                  isMarkedAsKnown ? msgs.unmarkAsKnown : msgs.markAsKnown,
                ),
                onClick: handleMarkAsKnown,
                isIconOnRight: true,
                fontSize: '16px',
                fontWeight: '500',
              },
            ],
          },
        ]),
    ...(isMarkedAsKnown || hideDifficultToggle
      ? []
      : [
          {
            dropdownItems: [
              {
                type: 'icon',
                iconCode: isMarkedAsDifficult ? 'difficultWordsFilled' : 'difficultWordsOutline',
                labelText: intl.formatMessage(
                  isMarkedAsDifficult ? msgs.unmarkAsDifficult : msgs.markAsDifficult,
                ),
                onClick: handleMarkAsDifficult,
                isIconOnRight: true,
                fontSize: '16px',
                fontWeight: '500',
              },
            ],
          },
        ]),
  ];

  return (
    <S.Container>
      {
        /* istanbul ignore next: please test in WEBSITE-7219 */
        experienceType === '2024' && (
          <BuddyLockedModal
            isOpen={isOpenLockedModal}
            onClose={() => setIsOpenLockedModal(false)}
          />
        )
      }
      {
        /* istanbul ignore next: please test in WEBSITE-7219 */
        experienceType === '2025' && isOpenLockedModal && (
          <FeatureUpsellModal
            tokenType="pronunciation_from_my_words"
            onClose={() => setIsOpenLockedModal(false)}
          />
        )
      }
      {isOpenPronunciationModal && (
        <PronunciationModal
          learnableId={learnableId}
          onActivityEngagement={onActivityEngagement}
          onClose={() => setIsOpenPronunciationModal(false)}
        />
      )}

      <S.Layout>
        {videoUrls.length > 0 && !isMobile && (
          <S.VideoColumn>
            <VideoSection videoUrls={videoUrls} />
          </S.VideoColumn>
        )}

        <S.ContentColumn>
          <S.HeaderContainer>
            {((isMarkedAsKnown && !hideMarkAsKnownToggle) ||
              (isMarkedAsDifficult && !(isMarkedAsKnown || hideDifficultToggle))) && (
              <S.HeaderRow>
                <S.BannerContainer>
                  {isMarkedAsKnown && !hideMarkAsKnownToggle && (
                    <S.Banner type="known">
                      <Icon name="tickFilled" />
                      <S.BannerLabel>{intl.formatMessage(msgs.known)}</S.BannerLabel>
                    </S.Banner>
                  )}
                  {isMarkedAsDifficult && !(isMarkedAsKnown || hideDifficultToggle) && (
                    <S.Banner type="difficult">
                      <Icon name="difficultWordsFilled" />
                      <S.BannerLabel>{intl.formatMessage(msgs.difficult)}</S.BannerLabel>
                    </S.Banner>
                  )}
                </S.BannerContainer>
              </S.HeaderRow>
            )}
          </S.HeaderContainer>

          <S.WordContainer>
            <S.DetailSectionWithEllipsis>
              <S.TargetLabel dir="auto">{cleanLanguageName(targetLanguageName)}</S.TargetLabel>
              <S.TargetValue dir="auto" data-testid="learnable_target">
                {targetValue}
              </S.TargetValue>

              {isMobile &&
                learnableId &&
                onMarkAsKnown &&
                onMarkAsDifficult &&
                dropdownSections.length > 0 && (
                  <S.EllipsisContainer data-testid="word_information_ellipsis">
                    <S.EllipsisPopout
                      positionX="left"
                      positionY="bottom"
                      dropdownSections={dropdownSections as DropdownSectionProps[]}
                    />
                  </S.EllipsisContainer>
                )}
            </S.DetailSectionWithEllipsis>
            <S.DetailSection>
              <S.SourceLabel dir="auto">{cleanLanguageName(sourceLanguageName)}</S.SourceLabel>
              <S.SourceValue dir="auto" data-testid="learnable_source">
                {sourceValue}
              </S.SourceValue>
            </S.DetailSection>
            {romanization && (
              <S.DetailSection>
                <S.DetailLabel>{intl.formatMessage(msgs.romanisation)}</S.DetailLabel>
                <S.DetailValue {...romanizationTextAttrs(romanization)}>
                  {romanization}
                </S.DetailValue>
              </S.DetailSection>
            )}
            {kana && (
              <S.DetailSection>
                <S.DetailLabel>{intl.formatMessage(msgs.kana)}</S.DetailLabel>
                <S.DetailValue>{kana}</S.DetailValue>
              </S.DetailSection>
            )}
            {literalTranslation && (
              <S.DetailSection>
                <S.DetailLabel>{intl.formatMessage(msgs.literalTranslation)}</S.DetailLabel>
                <S.DetailValue>{literalTranslation}</S.DetailValue>
              </S.DetailSection>
            )}
            <S.CountersContainer>
              <MyWordsCounter
                type="learned"
                sessions={wordCounters?.learned?.sessions || 0}
                isLoading={isLoading}
              />
              <MyWordsCounter
                type="heard"
                sessions={wordCounters?.heard?.sessions}
                occurrences={wordCounters?.heard?.occurrences}
                isLoading={isLoading}
              />
              <MyWordsCounter
                type="used"
                sessions={wordCounters?.used?.sessions}
                occurrences={wordCounters?.used?.occurrences}
                isLoading={isLoading}
              />
            </S.CountersContainer>
          </S.WordContainer>

          {videoUrls.length > 0 && isMobile && (
            <S.MobileVideoSection>
              <VideoSection videoUrls={videoUrls} />
            </S.MobileVideoSection>
          )}

          {audioUrls.length > 0 && (
            <S.AudioSection data-testid="audio_section" $hasBorderBottom={canUsePronunciationBuddy}>
              <S.AudioLabel>{intl.formatMessage(msgs.audio)}</S.AudioLabel>
              <S.AudioPlayersContainer>
                {audioUrls.map(audioUrl => (
                  <S.AudioPlayerWrapper key={`audio-${audioUrl}`}>
                    <AudioPlayer src={audioUrl} />
                  </S.AudioPlayerWrapper>
                ))}
              </S.AudioPlayersContainer>
            </S.AudioSection>
          )}

          {/* ALPHABET items are not supported for pronunciation practice - see API_TARGET_ITEM_TYPES.ALPHABET */}
          {canUsePronunciationBuddy && itemType !== 'ALPHABET' && (
            <S.PronunciationBuddyBanner
              data-testid="pronunciation_session_btn"
              onClick={() => {
                if (isPronunciationBuddyLocked) {
                  setIsOpenLockedModal(true);
                } else {
                  setIsOpenPronunciationModal(true);
                }
              }}
            >
              <S.BannerText>{intl.formatMessage(msgs.aiBuddy)}</S.BannerText>
              <S.BannerHeading>{intl.formatMessage(msgs.pronunciationPractice)}</S.BannerHeading>
              <S.BuddyImage name="buddyFullWaving" />
            </S.PronunciationBuddyBanner>
          )}
        </S.ContentColumn>

        {!isMobile &&
          learnableId &&
          onMarkAsKnown &&
          onMarkAsDifficult &&
          dropdownSections.length > 0 && (
            <S.EllipsisColumn
              data-testid="word_information_ellipsis"
              $hasBanners={isMarkedAsKnown || isMarkedAsDifficult}
            >
              <S.EllipsisPopout
                positionX="left"
                positionY="bottom"
                dropdownSections={dropdownSections as DropdownSectionProps[]}
              />
            </S.EllipsisColumn>
          )}
      </S.Layout>
    </S.Container>
  );
};

export default WordDetails;
